import {getFeatureGroupName, getFeatureUnit} from "../../../../misc/helpers";
import {getWizardPropertiesForFeatureId} from "../../../sqft/components/canvas/core/controls/resourseCanas";
import {isCornerCabinetShape} from "../../../sqft/components/canvas/core/cabinet/cabinetShapes";
import {
  CABINET_INTERIOR_SECTION_TYPE as SECTION_TYPE
} from "../../../sqft/constants/cabinetInteriorConstants";
import {isShapeTallCabinet} from "../../../sqft/helpers/cabinetBoxCompatibilityHelper";
import {
  calculateTallCabinetHinges,
  getExportedTallCabinetDoorHeights,
  getExportedTallCabinetInteriorQuantities
} from "../../../sqft/helpers/tallCabinetInteriorHelper";

export {getTallCabinetInteriorQuantities} from "../../../sqft/helpers/tallCabinetInteriorHelper";

const DEPTH = {
  shallow: 14,
  deep: 22,
};

const HINGE_TYPE = "hinge";

const FEATURE_IDS = {
  [`${SECTION_TYPE.shelf}-straight-${DEPTH.shallow}`]: 880733586,
  [`${SECTION_TYPE.shelf}-straight-${DEPTH.deep}`]: 880733585,
  [`${SECTION_TYPE.shelf}-corner-${DEPTH.shallow}`]: 880733588,
  [`${SECTION_TYPE.shelf}-corner-${DEPTH.deep}`]: 880733587,
  [`${SECTION_TYPE.drawer}-straight-${DEPTH.shallow}`]: 881583769,
  [`${SECTION_TYPE.drawer}-straight-${DEPTH.deep}`]: 371337000,
  [HINGE_TYPE]: 509356961,
};

const DERIVED_PARENT_KEY_PREFIX = "tall_cabinet_interior";

export const getTallCabinetInteriorFeatureGroups = (sqftPassedData, layout) => {
  return groupInteriorQuantitiesByFeature(sqftPassedData)
    .map(derivedGroup => findInteriorFeature(layout, derivedGroup))
    .map(feature => getFeatureGroup(layout, feature))
    .filter((featureGroup, index, featureGroups) => {
      return featureGroup && featureGroups.indexOf(featureGroup) === index;
    });
};

export const addTallCabinetInteriorFeatures = (
  sqftFeatures,
  sqftPassedData,
  layout,
  intl,
  fabricatorData,
  quoteIndex,
  previouslyAddedSqftFeatures = []
) => {
  const groupedQuantities = groupInteriorQuantitiesByFeature(sqftPassedData);

  groupedQuantities.forEach((derivedGroup) => {
    const feature = findInteriorFeature(layout, derivedGroup);
    const featureGroup = getFeatureGroup(layout, feature);

    if (!feature || !featureGroup) {
      console.warn("Tall cabinet interior feature not found:", derivedGroup);
      return;
    }

    const parentKey = `${DERIVED_PARENT_KEY_PREFIX}_${featureGroup.partId}`;
    const subPart = {
      part: featureGroup,
      text: getFeatureGroupName(featureGroup, intl, fabricatorData),
    };
    const sqftId = getDerivedSqftId(derivedGroup, quoteIndex);
    const previouslyAddedFeature = previouslyAddedSqftFeatures.find((existingFeature) => {
      return existingFeature
        && existingFeature.sqftId === sqftId
        && existingFeature.currentQuote === quoteIndex;
    });
    const wizardProperties = getWizardPropertiesForFeatureId(feature.dbFeature.id);
    const sqftFeature = {
      disabled: !!(previouslyAddedFeature && previouslyAddedFeature.disabled),
      isDisabledInPopup: false,
      feature,
      partId: featureGroup.partId,
      unit: getFeatureUnit(feature, intl, fabricatorData, derivedGroup.quantity),
      subPart,
      quantity: derivedGroup.quantity,
      quantityRounded: derivedGroup.quantity,
      initialQuantity: derivedGroup.quantity,
      sqftId,
      sqftIds: [sqftId],
      sqftQuantities: {[sqftId]: derivedGroup.quantity},
      qtCatName: derivedGroup.qtCatName,
      shouldShowEyeBall: false,
      ...wizardProperties,
    };

    if (!sqftFeatures[parentKey]) {
      sqftFeatures[parentKey] = {
        subPart,
        features: [],
      };
    }
    sqftFeatures[parentKey].features.push(sqftFeature);
  });

  return sqftFeatures;
};

const groupInteriorQuantitiesByFeature = (sqftPassedData) => {
  const cabinetShapes = sqftPassedData && Array.isArray(sqftPassedData.countertops)
    ? sqftPassedData.countertops
    : [];
  const groups = {};

  cabinetShapes
    .filter(isShapeTallCabinet)
    .forEach((shape) => {
      const quantities = getExportedTallCabinetInteriorQuantities(shape);
      const depth = getOriginalCabinetDepth(shape);
      const isCorner = isCornerCabinetShape(shape);

      addGroupedQuantity(groups, {
        type: SECTION_TYPE.shelf,
        depth,
        isCorner,
        quantity: quantities.shelves,
        qtCatName: shape.qtCatName,
      });
      addGroupedQuantity(groups, {
        type: SECTION_TYPE.drawer,
        depth,
        isCorner: false,
        quantity: quantities.drawers,
        qtCatName: shape.qtCatName,
      });
      addGroupedQuantity(groups, {
        type: HINGE_TYPE,
        quantity: calculateTallCabinetHinges(
          quantities,
          {doors: Number(shape.quantitySubParts && shape.quantitySubParts.doors) || 0},
          getExportedTallCabinetDoorHeights(shape)
        ),
        qtCatName: shape.qtCatName,
      });
    });

  return Object.values(groups);
};

const addGroupedQuantity = (groups, derivedGroup) => {
  if (!derivedGroup.quantity) {
    return;
  }

  const groupKey = derivedGroup.type === HINGE_TYPE
    ? HINGE_TYPE
    : [
      derivedGroup.type,
      derivedGroup.depth,
      derivedGroup.isCorner ? "corner" : "straight",
    ].join("-");

  if (!groups[groupKey]) {
    groups[groupKey] = {...derivedGroup};
    return;
  }

  groups[groupKey].quantity += derivedGroup.quantity;
};

const getOriginalCabinetDepth = (shape) => {
  const originalDepth = Number(shape && shape.shapeParams && shape.shapeParams.depth);

  if (!Number.isFinite(originalDepth)) {
    return DEPTH.deep;
  }

  return Math.abs(originalDepth - DEPTH.shallow) <= Math.abs(originalDepth - DEPTH.deep)
    ? DEPTH.shallow
    : DEPTH.deep;
};

const findInteriorFeature = (layout, derivedGroup) => {
  if (!layout || typeof layout.getFeatureById !== "function") {
    return null;
  }

  const shapeType = derivedGroup.isCorner ? "corner" : "straight";
  const featureId = derivedGroup.type === HINGE_TYPE
    ? FEATURE_IDS[HINGE_TYPE]
    : FEATURE_IDS[`${derivedGroup.type}-${shapeType}-${derivedGroup.depth}`];
  return featureId ? layout.getFeatureById(featureId, true) : null;
};

const getFeatureGroup = (layout, feature) => {
  if (!feature) {
    return null;
  }

  if (typeof feature.getParent === "function") {
    return feature.getParent();
  }

  return layout && layout.getFeatureGroupByPartId
    ? layout.getFeatureGroupByPartId(feature.partId)
    : null;
};

const getDerivedSqftId = (derivedGroup, quoteIndex) => {
  if (derivedGroup.type === HINGE_TYPE) {
    return `sqft-tall-cabinet-${HINGE_TYPE}-${quoteIndex}`;
  }

  const shapeType = derivedGroup.isCorner ? "corner" : "straight";
  return `sqft-tall-cabinet-${derivedGroup.type}-${shapeType}-${derivedGroup.depth}-${quoteIndex}`;
};
